import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Button } from '@educa-legal/ui';

const WHATSAPP_HREF = 'https://wa.me/5511989082892';

const credentials = ['Advogado', 'Contador', 'MBA em Direito Tributário'];

const verticalLinesPattern = {
  backgroundImage:
    'repeating-linear-gradient(90deg, #D4512A 0, #D4512A 1px, transparent 1px, transparent 96px)',
};

/**
 * Versão resumida do bloco do fundador, exibida na home.
 *
 * A bio completa vive em `/sobre-nos#fundador` (componente <Founder>).
 * Aqui temos um teaser editorial: foto + nome + ficha técnica curta
 * (anos, empresas) + tags de formação + CTA para a página completa.
 */
export function FounderTeaser() {
  const reduceMotion = useReducedMotion();
  const reveal = reduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 16 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, margin: '-48px' },
        transition: { duration: 0.55 },
      };

  return (
    <section className="relative py-20 md:py-24 bg-neutral-50 overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.025] pointer-events-none"
        style={verticalLinesPattern}
      />

      <div className="section-container relative">
        <motion.div
          className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-8 md:gap-12 items-center max-w-5xl"
          {...reveal}
        >
          {/* Foto */}
          <div className="relative w-44 sm:w-52 md:w-60 mx-auto md:mx-0">
            <div
              aria-hidden
              className="absolute -bottom-3 -right-3 w-full aspect-square bg-orange/15 rounded-full"
            />
            <div className="relative aspect-square rounded-full overflow-hidden ring-1 ring-neutral-200 shadow-[0_24px_48px_-18px_rgba(15,23,42,0.22)] bg-neutral-100">
              <img
                src="/foto-perfil-pedro.webp"
                alt="Pedro Guimarães Neto — fundador da Educa Legal"
                className="h-full w-full object-cover object-[center_15%]"
                loading="lazy"
                width={480}
                height={480}
              />
            </div>
          </div>

          {/* Conteúdo editorial */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-orange" />
              <span className="text-caption font-semibold uppercase tracking-[0.22em] text-neutral-700">
                Sobre Nós
              </span>
            </div>

            <h2 className="text-display-sm md:text-display-md font-bold text-neutral-950 leading-tight tracking-tight">
              Pedro Guimarães Neto
            </h2>
            <p className="mt-2 text-body-md text-neutral-500">
              Fundador e advogado especialista em direito educacional.
            </p>

            {/* Ficha técnica horizontal — números + empresas */}
            <dl className="mt-6 grid grid-cols-2 sm:grid-cols-[auto_auto_1fr] gap-x-6 gap-y-3 border-y border-neutral-200 py-4 max-w-xl">
              <div>
                <dt className="text-heading-xl font-bold text-orange tabular-nums leading-none">
                  +20
                </dt>
                <dd className="mt-1.5 text-caption text-neutral-500 leading-snug">
                  anos no direito
                </dd>
              </div>

              <div className="sm:border-l sm:border-neutral-200 sm:pl-6">
                <dt className="text-heading-xl font-bold text-orange tabular-nums leading-none">
                  +11
                </dt>
                <dd className="mt-1.5 text-caption text-neutral-500 leading-snug">
                  anos no setor educacional
                </dd>
              </div>

              <div className="col-span-2 sm:col-span-1 sm:border-l sm:border-neutral-200 sm:pl-6">
                <dt className="text-caption font-semibold uppercase tracking-[0.16em] text-neutral-500 mb-1">
                  Trajetória executiva
                </dt>
                <dd className="text-caption text-neutral-700 leading-snug">
                  Somos Educação · Isaac · Arco Educação
                </dd>
              </div>
            </dl>

            <p className="mt-5 text-body-md text-neutral-700 leading-relaxed max-w-xl">
              Pedro construiu sua trajetória <strong className="font-semibold text-neutral-900">dentro</strong> do setor educacional antes de atuar como advogado externo — o que lhe dá uma perspectiva que poucos advogados têm sobre a operação real de uma escola.
            </p>

            {/* Formação */}
            <div className="mt-5 flex flex-wrap items-center gap-2">
              {credentials.map((c) => (
                <span
                  key={c}
                  className="inline-flex items-center px-2.5 py-1 rounded-full text-caption font-medium border bg-orange-50 text-orange-700 border-orange-100"
                >
                  {c}
                </span>
              ))}
            </div>

            <div className="mt-6 flex flex-col items-start gap-4">
              <Link
                to="/sobre-nos"
                className="group inline-flex items-center gap-2 text-body-sm font-semibold text-orange-600 hover:text-orange-700 transition-colors"
              >
                Conheça a trajetória completa de Pedro
                <ArrowRight
                  size={14}
                  className="transition-transform duration-200 group-hover:translate-x-1"
                />
              </Link>
              <Button asChild variant="primary" size="lg">
                <a href={WHATSAPP_HREF} target="_blank" rel="noopener noreferrer">
                  Fale com Pedro
                </a>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
